import ActiveConfig from '../common/activeConfig'
import { EnvNames } from '../common/enums'
import { DEFAULT_API_PATH, INVALID_VALIDATOR_LABEL } from '../common/constants'
import {
  StringMap, ObjectMap
} from '../common/interfaces'
import PsmBridge from "../common/psm-bridge";

// DeepMerge code is sourced from: https://davidwalsh.name/javascript-deep-merge
const isMergeableObject = (val: any) => {
  let nonNullObject = val && typeof val === 'object'

  return nonNullObject
    && Object.prototype.toString.call(val) !== '[object RegExp]'
    && Object.prototype.toString.call(val) !== '[object Date]'
}

const emptyTarget = (val: any) => {
  return Array.isArray(val) ? [] : {}
}

const cloneIfNecessary = (value: any, optionsArgument: any) => {
  let clone = optionsArgument && optionsArgument.clone === true
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return (clone && isMergeableObject(value)) ? deepmerge(emptyTarget(value), value, optionsArgument) : value
}

const defaultArrayMerge = (target: ObjectMap, source: ObjectMap, optionsArgument: any) => {
  let destination = target.slice()
  source.forEach(function (e: any, i: number) {
    if (typeof destination[i] === 'undefined') {
      destination[i] = cloneIfNecessary(e, optionsArgument)
    } else if (isMergeableObject(e)) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      destination[i] = deepmerge(target[i], e, optionsArgument)
    } else if (target.indexOf(e) === -1) {
      destination.push(cloneIfNecessary(e, optionsArgument))
    }
  })
  return destination
}

const mergeObject = (target: ObjectMap, source: ObjectMap, optionsArgument: any) => {
  let destination: any = {}
  if (isMergeableObject(target)) {
    Object.keys(target).forEach(function (key) {
      destination[key] = cloneIfNecessary(target[key], optionsArgument)
    })
  }
  Object.keys(source).forEach(function (key) {
    if (!isMergeableObject(source[key]) || !target[key]) {
      destination[key] = cloneIfNecessary(source[key], optionsArgument)
    } else {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      destination[key] = deepmerge(target[key], source[key], optionsArgument)
    }
  })
  return destination
}

export const deepmerge = (target: ObjectMap, source: ObjectMap, optionsArgument?: any): ObjectMap => {
  let array = Array.isArray(source)
  let options = optionsArgument || { arrayMerge: defaultArrayMerge }
  let arrayMerge = options.arrayMerge || defaultArrayMerge

  if (array) {
    return Array.isArray(target) ? arrayMerge(target, source, optionsArgument) : cloneIfNecessary(source, optionsArgument)
  } else {
    return mergeObject(target, source, optionsArgument)
  }
}

export const logger = (...args: any[]) => {
  if (PsmBridge.dependencies?.debugModeEnabled) {
    console.log('=========================>>> PSM:', ...args)
  }
}

/*
  Util method that handles the value part of the key map
  - this should be handled in the Bridge
*/
export const convertStringToNestedObject = (
  obj: any,
  key: string,
  val: string,
  typing: string,
  enumArray?: any[]
): void => {
  const properties = key.split('.')
  const propLength = properties.length
  let current = obj

  const validated = validator(val, typing, enumArray)

  for (let index = 0; index < propLength; index++) {
    if (index < propLength - 1) {
      let next = current[properties[index]] ? current[properties[index]] : {}
      current[properties[index]] = next
      current = next
    } else {
      current[properties[index]] = validated
      current = validated
    }
  }
}

/**
 * Create an array of name of keys from EventMap in active-bundle-config JSON file
 * PLUS add extra important ModelUpdated to the list.
 * @returns string[] eventsDataFromCDN
 */
export const getKeysFromEventMap = (
  activeConfigEventMap: ObjectMap,
): string[] => {
  const eventsDataFromCDN: string[] = Object.keys(activeConfigEventMap)
  return eventsDataFromCDN
}

export const secondsInMilliseconds = (seconds: number): number => {
  return seconds * 1000
}

export const getApiUrlFromConfig = (): string => {
  const currentEnv: string = PsmBridge.psm.getConfig().psmEnvironment.toUpperCase()
  const currentDataAPI: StringMap = ActiveConfig.activeConfig.environment
  let envUrl: string

  switch (currentEnv) {
    case EnvNames.DEV:
      envUrl = currentDataAPI.dev
      break
    case EnvNames.QA:
      envUrl = currentDataAPI.qa
      break
    case EnvNames.INTEGRATION:
      envUrl = currentDataAPI.integration
      break
    case EnvNames.PROD:
      envUrl = currentDataAPI.prod
      break
  }
  return envUrl || DEFAULT_API_PATH
}

export const convertEnvForPayload = (oldEnv: string): string => {
  let convertedEnv: string

  switch (oldEnv) {
    case EnvNames.DEV:
      convertedEnv = 'dev'
      break
    case EnvNames.QA:
      convertedEnv = 'qa'
      break
    case EnvNames.INTEGRATION:
      convertedEnv = 'integration'
      break
    case EnvNames.PROD:
      convertedEnv = 'prod'
      break
  }
  return convertedEnv || oldEnv
}

/**
 * Check if object returning TRUE, if array, null, undefined,
 * string, number returns FALSE
 * @returns boolean
 */
export function isObject(object: ObjectMap): boolean {
  return object != null
    && typeof object === 'object'
    && typeof object !== 'undefined'
    && !Array.isArray(object)
}

/**
 * Compare objects and their key-value pairs, no order, and deep copy comparing.
 * Uses T generic type to extra safety.
 * @returns boolean
 */
export function checkObjEquality<T>(obj1: T | undefined, obj2: T): boolean {
  if (typeof obj1 === 'undefined') return false

  const props1: string[] = Object.getOwnPropertyNames(obj1)
  const props2: string[] = Object.getOwnPropertyNames(obj2)

  if (props1.length != props2.length) {
    return false
  }

  for (let i in props1) {
    let val1 = (obj1 as ObjectMap)[props1[i]]
    let val2 = (obj2 as ObjectMap)[props1[i]]

    let isObjects: boolean = isObject(val1) && isObject(val2)

    if (isObjects && !checkObjEquality<T>(val1, val2) || !isObjects && val1 !== val2) {
      return false
    }
  }
  return true
}

/**
 * Grab the value from a key inside nested object. Path is represented
 * by string separated by dot '.', i.e "user.info.name" and return their value
 * @returns any
 */
export function getValueFromNestedObjectInString(targetObj: any, pathString: string): any {
  return pathString.split('.').reduce((obj: any, key: string) =>
    (obj && obj[key] !== 'undefined') ? obj[key] : undefined, targetObj);
}

/**
 * Grab the value from a key inside nested object and redirect to new nested object
 * using a MAP (Schema version 1.1.5)
 * @returns object
 */
export function mapMetadata(
  programMetadata: ObjectMap,
  map: any,
): ObjectMap {
  let updatedState: ObjectMap = {}

  const toKeys: string[] = Object.keys(map)

  for (let stringPath of toKeys) {
    const toValue = getValueFromNestedObjectInString(programMetadata, stringPath)
    const { mapTo: toFinalPath, type: toTyping, enums: toEnumTypeArray = null } = map[stringPath]

    convertStringToNestedObject(updatedState, toFinalPath, toValue, toTyping, toEnumTypeArray)
  }
  return Object.assign({}, updatedState)
}


export function validator(value: any, typing: string, enumArray?: (number | string)[] | null): string | number | boolean | null {
  let checkedResult: string | number | boolean | null = null

  if (value === null ||
    typeof value === 'undefined' ||
    (typeof value === "string" && value.length === 0)) {
    return null
  }

  switch (typing) {
    case 'String':
      if (enumArray && enumArray.length > 0 && typeof value === "string") {
        checkedResult = checkEnumValidator(value, typing, enumArray)
      }
      else if (typeof value === "string" && value.length > 0 && !enumArray) {
        checkedResult = value
      }
      break;
    case 'Integer':
      if (enumArray && enumArray.length > 0 && typeof value === "number") {
        checkedResult = checkEnumValidator(value, typing, enumArray)
      }
      else if (typeof value === "number" && !enumArray) {
        checkedResult = Math.trunc(value)
      }
      break;
    case "Boolean":
      if (typeof value === "boolean") {
        checkedResult = value
      }
      break;
    default:
      break
  }
  return checkedResult
}

export function cleanValidatorPayload(object: any) {
  Object
    .entries(object)
    .forEach(([key, val]) => {
      if (val && typeof val === 'object') {
        cleanValidatorPayload(val)
      }
      if ((val && typeof val === 'object' && !Object.keys(val).length) || val === null) {
        delete object[key]
      }
    })
  return object
}

function checkEnumValidator(value: any, typing: string, enumArray: (number | string)[]): number | string {
  let isValid: boolean = false

  if (typing === 'String') {
    const isValid = typeof value === 'string' && enumArray.findIndex(item => value.toLowerCase() === item.toString().toLowerCase()) !== -1 && value.length > 0
    return isValid ? value.toLowerCase() : INVALID_VALIDATOR_LABEL + value
  }
  if (typing === 'Integer') {
    isValid = typeof value === 'number' && !isNaN(value) && enumArray.findIndex(item => value === item) !== -1
    return isValid ? value : null
  }
}

export function noSpecialChars(value: string): string {
  const lowerCasedValue = value.toLowerCase()
  const newValue = lowerCasedValue.replace(/\W|_/g, '')
  return newValue
}